import { connect, batch } from 'react-redux';
import PropTypes from 'prop-types';
import React, { Suspense } from 'react';
import { isBrowser } from 'ws-scripts/modules/environment';
import { trackEvent } from 'ddc-track-event';
import { Common } from 'ws-scripts/modules/common';
import { ErrorBoundary } from 'wsm-error-boundary';
import debounce from 'lodash.debounce';

import { setModalSettings } from '../actions/modalSettings';
import { setRenderSettings } from '../actions/renderSettings';
import { wiapiMediaUpdated, addFailedImages } from '../actions/media';
import { setIsBeingDragged } from '../actions/isBeingDragged';
import LoadingSpinner from '../components/LoadingSpinner';
import Slider from '../components/Slider';
import PhotoSwipeGalleryButton from '../components/photoswipe/PhotoSwipeGalleryButton';
import SingleImage from '../components/SingleImage';
import Cta from '../components/Cta';
import viewportSize from '../helpers/viewportSize';
import {
	replaceCarouselImageWithHTML,
	resetWIAPIContent
} from '../helpers/wiapi-html';
import { handleGalleryToolbarVisibility } from '../helpers/visibility';
import { WiapiImageInsertTracker } from '../helpers/wiapi-tracking';
import { NO_PHOTOS_IMAGE } from '../helpers/no-photos';

import './Widget.scss';
import { getMediaDisplay } from '../helpers/getMediaDisplay';
import { clearImageCaches } from '../helpers/imageResizer';
// import PanoramicModal from '../components/panoramic/PanoramicModal';

const PanoramicModal = React.lazy(() =>
	import(
		/* webpackChunkName: "PanoramicModal" , webpackPrefetch: true */ '../components/panoramic/PanoramicModal'
	)
);

export class WidgetContainer extends React.Component {
	constructor(props) {
		super(props);
		this.onResize = this.onResize.bind(this);
		this.trackGa = this.trackGa.bind(this);
		this.getMaxVisibleHeight = this.getMaxVisibleHeight.bind(this);
		this.updateSliderHeight = this.updateSliderHeight.bind(this);
		this.debounceUpdateSliderHeight =
			this.debounceUpdateSliderHeight.bind(this);
		this.onWiapiVehicleMediaUpdated =
			this.onWiapiVehicleMediaUpdated.bind(this);
		this.open360Modal = this.open360Modal.bind(this);
		this.close360Modal = this.close360Modal.bind(this);
		if (isBrowser) {
			this.wiapiTracker = new WiapiImageInsertTracker();
		}
		this.widgetElement = isBrowser
			? document.querySelector('[data-widget-name="ws-vehicle-media"]')
			: null;
		this.state = {
			disableAnimation: false,
			// if the imageDelayLoad pref is set, set the loading delay to true
			loadingDelayed: +this.props.prefs.imageDelayLoad > 0,
			// for desktop SSR, if multiple images we use this css class
			desktopSSRMultiImageClass: 'multi-image-desktop',
			sliderHeight: null,
			is360ModalOpen: false,
			isPanoramicImg: false,
			panoramicImgList: [],
			panoramicImageSrc: null
		};
	}

	componentDidMount() {
		// If imageDelayLoad preference is set, start the delay timer
		if (+this.props.prefs.imageDelayLoad > 0) {
			this.delayTimer = setTimeout(() => {
				// toggle delay loading off when the timer expires
				this.setState((prevState) => ({
					...prevState,
					loadingDelayed: false
				}));
			}, +this.props.prefs.imageDelayLoad);
		}

		const {
			widgetInfo: { deviceType },
			media: { images }
		} = this.props;

		const isMobile = deviceType === 'MOBILE';

		handleGalleryToolbarVisibility(images, 0, isMobile);

		this.setRenderSettings();
		this.props.updateModalSettings({ modalIsOpen: false });

		if (!this.debouncedResize) {
			/* eslint no-undef: 0 */
			this.debouncedResize = _.debounce(
				this.onResize,
				deviceType === 'MOBILE' ? 250 : 100
			);
		}
		window.addEventListener('resize', this.debouncedResize, false);

		window.addEventListener(
			'wiapiVehicleMediaUpdated',
			this.onWiapiVehicleMediaUpdated
		);

		// check if WIAPI media is available on mount
		this.onWiapiVehicleMediaUpdated();

		// check if exist any 360 image on last index
		const [lastImageObj] = images?.slice(-1);
		const img360 = new Image();
		img360.src = lastImageObj?.src;
		img360.onload = () => {
			const { naturalWidth: w, naturalHeight: h } = img360;
			const ratio = w / 2;
			if (ratio >= h) {
				const newImages = [...images];
				newImages.pop();
				this.setState({
					panoramicImgList: newImages,
					isPanoramicImg: true,
					panoramicImageSrc: lastImageObj?.src
				});
			}
		};

		// now on browser, we don't need this class (client will auto-adjust)
		this.setState({
			desktopSSRMultiImageClass: ''
		});
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.debouncedResize, false);

		window.removeEventListener(
			'wiapiVehicleMediaUpdated',
			this.onWiapiVehicleMediaUpdated
		);

		// if imageDelayLoad preference is set, toggle the loading delay off
		if (+this.props.prefs.imageDelayLoad > 0) {
			clearTimeout(this.delayTimer);
		}
	}

	onWiapiVehicleMediaUpdated() {
		const {
			modalSettings,
			updateModalSettings,
			renderSettings,
			updateRenderSettings,
			updateWiapiMedia,
			media: { imagesToDisplay: oldImagesToDisplay },
			prefs: {
				offsetSharedVehicleImageByOne,
				useStockPhotosForOffsiteInventory
			},
			widgetInfo: {
				vehicle: { vin }
			}
		} = this.props;

		// Return early if the WIAPI is not available.
		if (
			!(
				DDC &&
				DDC.PrivateAPI &&
				typeof DDC.PrivateAPI.getGalleryContent === 'function'
			)
		) {
			return;
		}

		// get the gallery content for the current vehicle
		const galleryContent = DDC.PrivateAPI.getGalleryContent(
			'vehicle-media',
			vin
		);

		// return early if no content was found
		if (!galleryContent) {
			return;
		}

		// return early if no media existed in the content
		if (
			!(
				galleryContent.primary.length > 0 ||
				galleryContent.secondary.length > 0 ||
				galleryContent.last.length > 0
			)
		) {
			return;
		}

		this.wiapiTracker.onImageLoad(
			modalSettings.modalIsOpen,
			modalSettings.currentIndex,
			renderSettings.currentIndex
		);

		const oldIndex = renderSettings.currentIndex || 0;

		batch(() => {
			this.setState((prevState) => ({
				...prevState,
				disableAnimation: true
			}));

			// insert the WIAPI media into the gallery
			updateWiapiMedia(
				galleryContent,
				offsetSharedVehicleImageByOne,
				useStockPhotosForOffsiteInventory
			);
		});

		// If the user hasn't interacted with the widget, go to the first primary image inserted (index = 0).
		// If they have interacted with the widget, update the index so they are seeing the same
		// image they were seeing before the insert.
		let newIndex = 0;
		// If a panoramic image was initially detected, update panoramicImgList
		// based on the new imagesToDisplay (which now include WIAPI images)
		if (this.state.isPanoramicImg && this.state.panoramicImageSrc) {
			const currentImagesToDisplay = this.props.media.imagesToDisplay;
			const newPanoramicImgList = currentImagesToDisplay.filter(
				(img) => img.src !== this.state.panoramicImageSrc
			);
			this.setState((prevState) => ({
				...prevState,
				panoramicImgList: newPanoramicImgList
			}));
		}

		if (modalSettings.modalIsOpen || renderSettings.currentIndex !== 0) {
			const newImagesToDisplay = this.props.media.imagesToDisplay;
			const foundIndex = newImagesToDisplay.findIndex(
				(elem) => elem.src === oldImagesToDisplay[oldIndex]?.src
			);
			// If we can't find the image, just go to the first image. This could occur when going
			// from no-photo-lrg.png (no vehicle images) to a WIAPI-inserted image.
			newIndex = foundIndex !== -1 ? foundIndex : 0;
		}

		batch(() => {
			updateRenderSettings({ currentIndex: newIndex });
			updateModalSettings({ currentIndex: newIndex });
			this.setState((prevState) => ({
				...prevState,
				disableAnimation: false
			}));
		});

		// This is a workaround for an issue where inserting slides async separated by 500 ms
		// would lead to the carousel getting stuck at zero height intermittently. Firing the resize event
		// allows the carousel to resize the height. This should be fixed in the library, but
		// until that happens, this will have to suffice. It adds extra renders, but it's only
		// called when WIAPI slides are inserted. Since the flash to zero height can be dependent on
		// image download times, we check for 10 seconds after WIAPI images are updated.
		let elapsedTime = 0;
		const interval = 250;
		const intervalId = setInterval(() => {
			elapsedTime += interval;
			if (elapsedTime >= 10000) {
				clearInterval(intervalId);
			}
			if (isBrowser) {
				const elem = this.widgetElement.querySelector('.slider-frame');
				if (elem && elem.offsetHeight === 0) {
					window.dispatchEvent(new Event('resize'));
				}
			}
		}, interval);

		// if imageDelayLoad preference is set, clear the delayTimer and toggle the loading delay
		if (+this.props.prefs.imageDelayLoad > 0) {
			clearTimeout(this.delayTimer);
			this.setState((prevState) => ({
				...prevState,
				loadingDelayed: false
			}));
		}
	}

	onResize() {
		clearImageCaches();
		batch(() => {
			this.setRenderSettings();
			this.updateSliderHeight();
		});
	}

	setRenderSettings() {
		const {
			prefs,
			renderSettings,
			widgetInfo: { deviceType },
			media
		} = this.props;
		const { viewportWidth, viewportHeight } = viewportSize();
		const innerWidth = viewportWidth;
		const innerHeight = viewportHeight;

		const { threeUpDisplay, imageMode, responsiveImage, numSlides } =
			getMediaDisplay({
				prefs,
				imagesToDisplay: media?.images,
				deviceType
			});

		// Get previous state
		let { currentIndex = 0 } = renderSettings;

		// If we've got no overflow on a 3-up display
		if (threeUpDisplay && numSlides <= 3) {
			// Reset slides to beginning to show all in 3-up
			currentIndex = 0;

			// Don't allow the gallery to change the slide index
			this.props.updateModalSettings({ persistIndex: false });
		} else {
			// Allow the current gallery slide to be shown first when returning to vehicle page
			this.props.updateModalSettings({ persistIndex: true });
		}

		this.props.updateRenderSettings({
			innerWidth,
			innerHeight,
			threeUpDisplay,
			imageMode,
			responsiveImage,
			currentIndex
		});
	}

	getMaxVisibleHeight() {
		const sliderEle = this.sliderCmp?.sliderRef?.current;
		const visibleSlides = sliderEle?.querySelectorAll('.slide-visible');
		const visibleHeights = [];

		visibleSlides?.forEach((slide) => {
			if (slide?.clientHeight) {
				visibleHeights.push(slide?.clientHeight);
			}
		});

		return visibleHeights.length ? Math.max(...visibleHeights) : null;
	}

	updateSliderHeight() {
		const sliderHeight = this.getMaxVisibleHeight();
		this.setState({ sliderHeight });
	}

	// eslint-disable-next-line react/sort-comp
	debounceUpdateSliderHeight = debounce(this.updateSliderHeight, 200);

	trackGa(trackingObj) {
		const defaultTrackingObj = {
			widgetState: `default (${
				this.props.renderSettings.threeUpDisplay
					? 'tile view'
					: 'full view'
			})`
		};

		trackEvent('ws-vehicle-media', this.props.widgetInfo.windowId, {
			...defaultTrackingObj,
			...trackingObj
		});
	}

	open360Modal() {
		this.setState({ is360ModalOpen: true });
	}

	close360Modal() {
		this.setState({ is360ModalOpen: false });
	}

	render() {
		const {
			media: { videos, videoOrder },
			widgetInfo,
			widgetInfo: { deviceType, pathParameters, parameterMap },
			prefs: {
				galleryIconColor,
				ctaHref1,
				ctaIcon1,
				ctaBtnStyle,
				ctaLabel1,
				ctaIsVehicleLink1,
				ctaIsDialogLink1,
				ctaDialogTitle1,
				ctaDialogWidth1,
				offsetSharedVehicleImageByOne,
				useStockPhotosForOffsiteInventory
			},
			updateRenderSettings,
			updateModalSettings,
			updateIsBeingDragged,
			renderSettings: {
				innerHeight,
				innerWidth,
				imageMode,
				responsiveImage,
				threeUpDisplay,
				currentIndex,
				preloadImages
			},
			modalSettings,
			isBeingDragged,
			addFailedImage
		} = this.props;

		const { sliderHeight } = this.state;
		const isMobile = deviceType === 'MOBILE';
		const { _vehicleMediaSSR } = parameterMap;

		return (
			<ErrorBoundary newRelicPageAction="ws-vehicle-media: error boundary">
				<Common
					render={({ sitemap, labels, flags, prefs }) => {
						// TODO: remove condition once/if we keep SSR permanent
						const serverSideRender =
							_vehicleMediaSSR === 'true' ||
							flags['ws-vehicle-media-server-render'];

						const idcImgAttrFlag =
							flags[
								'ws-vehicle-media-toggle-idc-image-attributes'
							];

						if (
							this.state.loadingDelayed ||
							(!serverSideRender && !innerHeight)
						) {
							return <LoadingSpinner />;
						}

						const {
							media: { imagesToDisplay }
						} = this.props;

						const shouldShow360Modal =
							isBrowser && prefs.show360Image === 'true';

						const { panoramicImgList, isPanoramicImg } = this.state;

						const imageList = isPanoramicImg
							? panoramicImgList
							: imagesToDisplay;

						const galleryProps = {
							labels,
							images: imageList,
							widgetInfo,
							modalSettings,
							videos,
							videoOrder,
							isBeingDragged,
							disableAnimation: this.state.disableAnimation,
							sliderHeight,
							// renderSettings,
							currentIndex,
							innerHeight,
							innerWidth,
							imageMode,
							responsiveImage,
							preloadImages,
							threeUpDisplay,
							updateRenderSettings,
							updateModalSettings,
							updateIsBeingDragged,
							updateSliderHeight: this.debounceUpdateSliderHeight,

							// Prefs for PhotoSwipe
							galleryIconColor,
							onCurrentIndexChange: (newIndex, cb) => {
								if (
									newIndex !==
									this.props.renderSettings.currentIndex
								) {
									updateRenderSettings({
										currentIndex: newIndex
									});
								}
								if (cb) {
									cb(newIndex);
								}
							},
							onPhotoClick:
								imagesToDisplay.length > 0 &&
								imagesToDisplay[0].src !== NO_PHOTOS_IMAGE.src
									? (
											clickedIndex = this.props
												.renderSettings.currentIndex
									  ) => {
											const item =
												imagesToDisplay[clickedIndex];

											updateIsBeingDragged(false);

											this.trackGa({
												dataToExcludeFromWidgetUserEvent:
													{
														currentItem:
															currentIndex + 1,
														totalItems:
															imagesToDisplay.length
													},
												element: 'Photo Carousel',
												result:
													item.type === 'html'
														? 'Activates dynamic gallery content'
														: 'Opens gallery modal'
											});

											updateRenderSettings({
												currentIndex: clickedIndex
											});

											if (item.type === 'html') {
												resetWIAPIContent(
													this.widgetElement
												);

												replaceCarouselImageWithHTML(
													this.widgetElement,
													item,
													isMobile
												);
											} else {
												updateModalSettings({
													modalIsOpen: true,
													videoIsOpen: false,
													spin360IsOpen: false,
													currentIndex: clickedIndex
												});
											}
									  }
									: () => {},
							trackGa: this.trackGa,
							onImageError: (originalSrc, _actualSrc) => {
								addFailedImage(
									originalSrc,
									offsetSharedVehicleImageByOne,
									useStockPhotosForOffsiteInventory
								);
							},
							open360Modal: this.open360Modal
						};

						const slider = (
							<Slider
								ref={(sc) => {
									this.sliderCmp = sc;
								}}
								{...galleryProps}
								idcImgAttrFlag={idcImgAttrFlag}
							/>
						);

						const photoSwipeGalleryButton = (
							<PhotoSwipeGalleryButton
								{...galleryProps}
								{...{ isMobile }}
								isPanoramicImg={isPanoramicImg}
							/>
						);

						const params = new Map();
						if (ctaIsVehicleLink1 === 'true') {
							params.set('itemId', pathParameters.vehicleId);
						}

						const cta1 = (
							<Cta
								btnStyle={ctaBtnStyle}
								href={sitemap.get(ctaHref1)}
								icon={ctaIcon1}
								label={ctaLabel1}
								params={params}
								dialog={ctaIsDialogLink1 === 'true'}
								dialogTitle={ctaDialogTitle1}
								dialogWidth={ctaDialogWidth1}
								isMobile={isMobile}
								trackGa={this.trackGa}
								key="CTA1"
							/>
						);

						const ctaList = [cta1];

						const ctaContainerClasses = [
							'cta-container',
							'd-flex',
							'd-print-none',
							'position-absolute'
						];
						if (!isMobile) {
							ctaContainerClasses.push('my-4');
						}

						const ctas = ctaList.join('').length ? (
							<div className={ctaContainerClasses.join(' ')}>
								{ctaList}
							</div>
						) : null;

						const firstImage = imagesToDisplay[0];
						const [lastImage] = imagesToDisplay?.slice(-1);
						const singleImage =
							!isMobile && isBrowser ? (
								<SingleImage
									image={firstImage ? firstImage.src : ''}
									uuid={pathParameters.vehicleId}
									onClick={() => {
										updateIsBeingDragged(false);

										this.trackGa({
											element: 'Photo',
											result: 'Opens gallery modal',
											widgetState:
												firstImage.type === 'html'
													? 'dynamic content (thumbnail)'
													: 'default (thumbnail)'
										});

										updateModalSettings({
											modalIsOpen: true,
											videoIsOpen: false,
											spin360IsOpen: false,
											persistIndex: false, // This allows currentIndex to actually work.
											currentIndex: 0
										});
									}}
									trackGa={this.trackGa}
								/>
							) : null;
						// The data-location attribute is used by the Third Party API
						// as a consistent named location for third parties to inject
						// content of a particular type. More information is available at:
						// https://ghe.coxautoinc.com/DDC-WebPlatform/ddc-js-api
						let mediaContent = (
							<div
								className="media-content"
								data-location="vehicle-media-container"
							>
								{ctas}
								{slider}
								{singleImage}
								{photoSwipeGalleryButton}
								{shouldShow360Modal && (
									<Suspense fallback={<div />}>
										<PanoramicModal
											isOpen={this.state.is360ModalOpen}
											closeModal={this.close360Modal}
											isMobile={isMobile}
											lastImage={lastImage}
										/>
									</Suspense>
								)}
							</div>
						);

						if (!isMobile) {
							mediaContent = (
								<div
									className={`media-content ${
										imagesToDisplay.length > 1
											? this.state
													.desktopSSRMultiImageClass
											: ''
									}`}
									data-location="vehicle-media-container"
								>
									{ctas}
									{slider}
									{singleImage}
									{photoSwipeGalleryButton}
									{shouldShow360Modal && (
										<Suspense fallback={<div />}>
											<PanoramicModal
												isOpen={
													this.state.is360ModalOpen
												}
												closeModal={this.close360Modal}
												isMobile={isMobile}
												lastImage={lastImage}
											/>
										</Suspense>
									)}
								</div>
							);
						}
						return mediaContent;
					}}
				/>
			</ErrorBoundary>
		);
	}
}

WidgetContainer.propTypes = {
	prefs: PropTypes.shape({
		threeUpRatio: PropTypes.string,
		responsiveDesktopImage: PropTypes.string,
		galleryIconColor: PropTypes.string,
		ctaBtnStyle: PropTypes.string,
		ctaHref1: PropTypes.string,
		ctaIcon1: PropTypes.string,
		ctaLabel1: PropTypes.string,
		ctaIsVehicleLink1: PropTypes.string,
		ctaIsDialogLink1: PropTypes.string,
		ctaDialogTitle1: PropTypes.string,
		ctaDialogWidth1: PropTypes.string,
		offsetSharedVehicleImageByOne: PropTypes.string,
		useStockPhotosForOffsiteInventory: PropTypes.string,
		usedVehicleTerm: PropTypes.string,
		imageDelayLoad: PropTypes.number,
		show360Image: PropTypes.string
	}),
	media: PropTypes.shape({
		images: PropTypes.arrayOf(
			PropTypes.shape({
				src: PropTypes.string,
				thumbnail: PropTypes.string
			})
		).isRequired,
		imagesToDisplay: PropTypes.arrayOf(
			PropTypes.shape({
				callback: PropTypes.func,
				html: PropTypes.string,
				src: PropTypes.string,
				thumbnail: PropTypes.string,
				type: PropTypes.string
			})
		).isRequired,
		videos: PropTypes.shape({}),
		videoOrder: PropTypes.arrayOf(
			PropTypes.oneOfType([PropTypes.string, PropTypes.number])
		)
	}).isRequired,
	widgetInfo: PropTypes.shape({
		windowId: PropTypes.string.isRequired,
		deviceType: PropTypes.string,
		vehicle: PropTypes.shape({
			accountId: PropTypes.string,
			vin: PropTypes.string
		}).isRequired,
		accountId: PropTypes.string,
		pathParameters: PropTypes.shape({
			vehicleId: PropTypes.string
		}).isRequired,
		parameterMap: PropTypes.shape({
			_vehicleMediaSSR: PropTypes.string
		}).isRequired
	}).isRequired,
	renderSettings: PropTypes.shape({
		innerHeight: PropTypes.number,
		innerWidth: PropTypes.number,
		imageMode: PropTypes.bool,
		responsiveImage: PropTypes.bool,
		threeUpDisplay: PropTypes.bool,
		currentIndex: PropTypes.number,
		preloadImages: PropTypes.arrayOf(
			PropTypes.shape({
				path: PropTypes.string.isRequired
			})
		)
	}),
	modalSettings: PropTypes.shape({
		modalIsOpen: PropTypes.bool,
		currentIndex: PropTypes.number
	}),
	updateRenderSettings: PropTypes.func.isRequired,
	updateModalSettings: PropTypes.func.isRequired,
	updateIsBeingDragged: PropTypes.func.isRequired,
	isBeingDragged: PropTypes.bool,
	updateWiapiMedia: PropTypes.func.isRequired,
	addFailedImage: PropTypes.func.isRequired
};

WidgetContainer.defaultProps = {
	prefs: {
		threeUpRatio: '1',
		responsiveDesktopImage: 'true',
		offsetSharedVehicleImageByOne: 'false',
		ctaIcon1: 'share-default'
	},
	renderSettings: {
		innerHeight: null,
		innerWidth: null,
		imageMode: false,
		responsiveImage: true,
		threeUpDisplay: false,
		currentIndex: 0
	},
	modalSettings: {
		modalIsOpen: false
	},
	isBeingDragged: false
};

const mapStateToProps = (state) => {
	return {
		prefs: state.prefs,
		media: state.media,
		widgetInfo: state.widgetInfo,
		renderSettings: state.renderSettings,
		modalSettings: state.modalSettings,
		isBeingDragged: state.isBeingDragged,
		wiapiGalleryContent: state.wiapi
	};
};

const mapDispatchToProps = (dispatch) => ({
	updateWiapiMedia: (
		galleryContent,
		offsetSharedVehicleImageByOne,
		useStockPhotosForOffsiteInventory
	) => {
		dispatch(
			wiapiMediaUpdated(
				galleryContent,
				offsetSharedVehicleImageByOne,
				useStockPhotosForOffsiteInventory
			)
		);
	},
	updateRenderSettings: (props) => {
		dispatch(setRenderSettings(props));
	},
	updateModalSettings: (props) => {
		dispatch(setModalSettings(props));
	},
	updateIsBeingDragged: (isBeingDragged) => {
		dispatch(setIsBeingDragged(isBeingDragged));
	},
	addFailedImage: (
		imageSrc,
		offsetSharedVehicleImageByOne,
		useStockPhotosForOffsiteInventory
	) => {
		dispatch(
			addFailedImages(
				[imageSrc],
				offsetSharedVehicleImageByOne,
				useStockPhotosForOffsiteInventory
			)
		);
	}
});

const Widget = connect(mapStateToProps, mapDispatchToProps)(WidgetContainer);

export default Widget;
